import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createTheme, ThemeProvider } from '@material-ui/core';

export type ThemeValue = 'light' | 'dark' | 'light-automation' | 'dark-automation';

export interface ThemeContextValue {
  theme: ThemeValue;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

export function GoFormzThemeProvider(props) {
  const theme = useThemeFromLocalStorage();

  useLayoutEffect(() => {
    setThemeInDom(theme);
  }, [theme]);

  const isDarkTheme = theme === 'dark' || theme === 'dark-automation';
  const muiTheme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: ['"Nunito Sans"', '"Helvetica Neue"', 'Arial', 'Helvetica', 'sans-serif'].join(','),
          button: {
            textTransform: 'none'
          }
        },
        palette: {
          type: isDarkTheme ? 'dark' : 'light',
          primary: {
            main: '#0b99c2'
          },
          secondary: {
            main: '#ff7a47'
          },
          background: {
            default: isDarkTheme ? '#1b1e1f' : '#f9f9f9'
          }
        }
      }),
    [theme]
  );

  return (
    <ThemeContext.Provider value={{ theme }}>
      <ThemeProvider theme={muiTheme}>{props.children}</ThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error(`useTheme must be used within GoFormzThemeProvider`);
  }
  return context;
}

function useThemeFromLocalStorage() {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  useEffect(() => {
    window.setTheme = (t: ThemeValue) => {
      localStorage.setItem('theme', t);
      setTheme(t);
    };
    window.addEventListener('storage', (ev: StorageEvent) => {
      if (ev.key === 'theme') {
        setTheme(localStorage.getItem('theme'));
      }
    });
  }, []);
  return theme as ThemeValue;
}

const themeMap: { [key in ThemeValue]: string } = Object.freeze({
  ['dark']: 'dark-theme',
  ['light']: 'light-theme',
  ['dark-automation']: 'dark-theme automation-theme',
  ['light-automation']: 'light-theme automation-theme'
});

export function setThemeInDom(theme: ThemeValue) {
  document.documentElement.className = themeMap[theme];
}
